import React, { useRef, useState } from "react";
import { cornerstone, cornerstoneWADOImageLoader } from "../initCornerstone";

const DicomViewer = () => {
  const elementRef = useRef(null);
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState({});
  const [numFrames, setNumFrames] = useState(1);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [baseImageId, setBaseImageId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const playIntervalRef = useRef(null);

  // ✅ Controls
  const [zoom, setZoom] = useState(1);
  const [contrast, setContrast] = useState(1000);
  const [speed, setSpeed] = useState(1); // 0.25 → 2x

  // 🏷️ Tagging state
  const [tags, setTags] = useState({}); // { frameIndex: [{x, y, label}] }
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagPosition, setTagPosition] = useState({ x: 0, y: 0 });

  const displayMetadata = (dataset) => {
    setMetadata({
      PatientName: dataset.string("x00100010") || "N/A",
      PatientID: dataset.string("x00100020") || "N/A",
      PatientBirthDate: dataset.string("x00100030") || "N/A",
      PatientSex: dataset.string("x00100040") || "N/A",
      StudyInstanceUID: dataset.string("x0020000D") || "N/A",
      StudyID: dataset.string("x00200010") || "N/A",
      StudyDate: dataset.string("x00080020") || "N/A",
      StudyTime: dataset.string("x00080030") || "N/A",
      StudyDescription: dataset.string("x00081030") || "N/A",
      AccessionNumber: dataset.string("x00080050") || "N/A",
      Modality: dataset.string("x00080060") || "N/A",
      Manufacturer: dataset.string("x00080070") || "N/A",
      ManufacturerModelName: dataset.string("x00081090") || "N/A",
      DeviceSerialNumber: dataset.string("x00181000") || "N/A",
      SoftwareVersions: dataset.string("x00181020") || "N/A",
      ProtocolName: dataset.string("x00181030") || "N/A",
      Rows: dataset.uint16("x00280010") || "N/A",
      Columns: dataset.uint16("x00280011") || "N/A",
      NumberOfFrames: dataset.string("x00280008") || "1",
      BitsAllocated: dataset.uint16("x00280100") || "N/A",
      BitsStored: dataset.uint16("x00280101") || "N/A",
      PixelRepresentation: dataset.uint16("x00280103") || "N/A",
      PhotometricInterpretation: dataset.string("x00280004") || "N/A",
      SamplesPerPixel: dataset.uint16("x00280002") || "N/A",
      PixelSpacing: dataset.string("x00280030") || "N/A",
      SOPClassUID: dataset.string("x00080016") || "N/A",
      SOPInstanceUID: dataset.string("x00080018") || "N/A",
      TransferSyntaxUID: dataset.string("x00020010") || "N/A",
      ImplementationClassUID: dataset.string("x00020012") || "N/A",
      ImplementationVersionName: dataset.string("x00020013") || "N/A",
    });
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setError(null);
    clearInterval(playIntervalRef.current);
    setPlaying(false);

    try {
      const file = files[0];
      const id = cornerstoneWADOImageLoader.wadouri.fileManager.add(file);
      setBaseImageId(id);

      const element = elementRef.current;
      cornerstone.enable(element);

      const image = await cornerstone.loadAndCacheImage(id);
      cornerstone.displayImage(element, image);

      const dataset = image.data;
      displayMetadata(dataset);

      const frames = parseInt(dataset.string("x00280008")) || 1;
      setNumFrames(frames);
      setCurrentFrame(0);
      setTags({});
    } catch (err) {
      console.error(err);
      setError(err.message || String(err));
    }
  };

  const showFrame = async (frameIndex) => {
    if (!baseImageId) return;
    try {
      const frameImageId = `${baseImageId}?frame=${frameIndex}`;
      const image = await cornerstone.loadAndCacheImage(frameImageId);
      cornerstone.displayImage(elementRef.current, image);
      setCurrentFrame(frameIndex);
    } catch (err) {
      console.error(err);
      setError(err.message || String(err));
      clearInterval(playIntervalRef.current);
      setPlaying(false);
    }
  };

  // 🟢 Play/Pause logic
  const handlePlay = () => {
    if (numFrames <= 1) return;

    if (playing) {
      clearInterval(playIntervalRef.current);
      setPlaying(false);
    } else {
      setPlaying(true);
      const interval = 1000 / (speed * 4);
      playIntervalRef.current = setInterval(() => {
        setCurrentFrame((prev) => {
          const next = (prev + 1) % numFrames;
          showFrame(next);
          return next;
        });
      }, interval);
    }
  };

  const handleSliderChange = (e) => {
    const frameIndex = parseInt(e.target.value);
    showFrame(frameIndex);
  };

  // ✅ Zoom / Contrast / Speed
  const handleZoomChange = (e) => {
    const newZoom = parseFloat(e.target.value);
    setZoom(newZoom);
    const viewport = cornerstone.getViewport(elementRef.current);
    viewport.scale = newZoom;
    cornerstone.setViewport(elementRef.current, viewport);
  };

  const handleContrastChange = (e) => {
    const newContrast = parseFloat(e.target.value);
    setContrast(newContrast);
    const viewport = cornerstone.getViewport(elementRef.current);
    viewport.voi.windowWidth = newContrast;
    cornerstone.setViewport(elementRef.current, viewport);
  };

  const handleSpeedChange = (e) => {
    const newSpeed = parseFloat(e.target.value);
    setSpeed(newSpeed);
    if (playing) {
      clearInterval(playIntervalRef.current);
      const interval = 1000 / (newSpeed * 4);
      playIntervalRef.current = setInterval(() => {
        setCurrentFrame((prev) => {
          const next = (prev + 1) % numFrames;
          showFrame(next);
          return next;
        });
      }, interval);
    }
  };

  // 🏷️ Tagging
  const handleImageClick = (e) => {
    if (!elementRef.current || playing) return;
    const rect = elementRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setTagPosition({ x, y });
    setShowTagInput(true);
  };

  const handleAddTag = (label) => {
    if (!label.trim()) return setShowTagInput(false);
    setTags((prev) => ({
      ...prev,
      [currentFrame]: [
        ...(prev[currentFrame] || []),
        { x: tagPosition.x, y: tagPosition.y, label },
      ],
    }));
    setShowTagInput(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white flex flex-col items-center py-6">
      <h1 className="text-3xl font-bold mb-6 text-cyan-400 tracking-wide drop-shadow-lg">
        🩺 DICOM Image Viewer
      </h1>

      {/* Upload Section */}
      <div className="mb-4">
        <label className="px-5 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg cursor-pointer transition">
          Upload DICOM File
          <input
            type="file"
            ref={fileInputRef}
            accept=".dcm,.dicom,application/dicom"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>
      </div>

      {/* Viewer + Metadata */}
      <div className="flex flex-col lg:flex-row gap-6 w-[90%] max-w-[1400px] justify-center">
        <div
          ref={elementRef}
          onClick={handleImageClick}
          className="relative w-[900px] h-[900px] bg-black border-2 border-gray-700 rounded-xl shadow-2xl overflow-hidden"
        >
          {!baseImageId && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-lg">
              Upload a DICOM file to view
            </div>
          )}

          {/* 🏷️ Render tags for current frame */}
          {(tags[currentFrame] || []).map((tag, i) => (
            <div
              key={i}
              className="absolute flex flex-col items-center"
              style={{ left: tag.x, top: tag.y, transform: "translate(-50%, -100%)" }}
            >
              <div className="w-3 h-3 bg-cyan-400 rounded-full border border-white"></div>
              <div className="mt-1 text-xs bg-cyan-600 px-2 py-0.5 rounded">
                {tag.label}
              </div>
            </div>
          ))}

          {/* Input popup for adding tag */}
          {showTagInput && (
            <div
              className="absolute bg-gray-800 p-2 rounded-lg border border-cyan-500 shadow-lg"
              style={{
                left: tagPosition.x,
                top: tagPosition.y,
                transform: "translate(-50%, -120%)",
              }}
            >
              <input
                type="text"
                placeholder="Enter tag"
                className="px-2 py-1 text-black rounded w-32"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddTag(e.target.value);
                  if (e.key === "Escape") setShowTagInput(false);
                }}
                onBlur={(e) => handleAddTag(e.target.value)}
              />
            </div>
          )}
        </div>

        {Object.keys(metadata).length > 0 && (
          <div className="w-full lg:w-[550px] bg-gray-800/70 border border-gray-700 rounded-xl p-4 backdrop-blur-md shadow-inner overflow-y-auto max-h-[900px]">
            <h3 className="font-semibold text-cyan-400 mb-2 border-b border-gray-700 pb-1 sticky top-0 bg-gray-800/90 backdrop-blur-md z-10">
              DICOM Metadata
            </h3>
            <div className="grid grid-cols-1 gap-2 text-sm">
              {Object.entries(metadata).map(([key, value]) => (
                <div key={key}>
                  <span className="text-gray-400">{key}:</span>{" "}
                  <span className="font-medium text-white">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="text-red-400 mt-3 text-sm font-medium">⚠ {error}</div>
      )}

      {/* Controls */}
      {numFrames > 1 && (
        <div className="mt-6 w-[900px] flex flex-col items-center gap-4 bg-gray-800/70 p-4 rounded-xl border border-gray-700 backdrop-blur-md">
          <div className="flex flex-wrap items-center justify-between w-full gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handlePlay}
                className={`px-4 py-1.5 rounded-md font-medium transition ${
                  playing
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-cyan-600 hover:bg-cyan-700"
                }`}
              >
                {playing ? "⏸ Pause" : "▶ Play"}
              </button>
              <span className="text-gray-300 text-sm">
                Frame {currentFrame + 1} / {numFrames}
              </span>
            </div>

            <div className="flex items-center gap-5 text-sm">
              <div className="flex flex-col items-center">
                <label className="text-gray-300">🔍 Zoom</label>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={handleZoomChange}
                  className="accent-cyan-500"
                />
                <span className="text-gray-400">{zoom.toFixed(1)}x</span>
              </div>

              <div className="flex flex-col items-center">
                <label className="text-gray-300">🎨 Contrast</label>
                <input
                  type="range"
                  min="100"
                  max="3000"
                  step="50"
                  value={contrast}
                  onChange={handleContrastChange}
                  className="accent-cyan-500"
                />
                <span className="text-gray-400">{contrast}</span>
              </div>

              <div className="flex flex-col items-center">
                <label className="text-gray-300">⚡ Speed</label>
                <input
                  type="range"
                  min="0.25"
                  max="2"
                  step="0.25"
                  value={speed}
                  onChange={handleSpeedChange}
                  className="accent-cyan-500"
                />
                <span className="text-gray-400">{speed.toFixed(2)}x</span>
              </div>
            </div>
          </div>

          <input
            type="range"
            min="0"
            max={numFrames - 1}
            value={currentFrame}
            onChange={handleSliderChange}
            className="w-full accent-cyan-500"
          />
        </div>
      )}
    </div>
  );
};

export default DicomViewer;
